import React, {useEffect, useState, useRef, useContext} from 'react';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import Swal from 'sweetalert2';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import '../css/userProfilePage.css';
import logo from '../images/logo_small.png';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faComment } from "@fortawesome/free-regular-svg-icons";
import LoadingSpinner from "../components/LoadingSpinner";
import PostComment from "../components/PostComment";
import {faHeart, faShareAlt} from "@fortawesome/free-solid-svg-icons";
import i18n from "../i18n";
import moment from "moment-timezone";
import api from "../api";
import { AuthContext } from "../context/AuthContext";
import DOMPurify from "dompurify";

function UserProfilePage() {
    const { userId } = useParams();
    const [userProfile, setUserProfile] = useState(null);
    const [userPosts, setUserPosts] = useState([]);
    const [userAchievements, setUserAchievements] = useState([]);
    const [isCurrentUser, setIsCurrentUser] = useState(false);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [loggedInUserId, setLoggedInUserId] = useState(null);
    const [showCommentsMap, setShowCommentsMap] = useState({});
    const [likedPosts, setLikedPosts] = useState(new Set());
    const lastPostElementRef = useRef();
    const observerInstance = useRef();
    const { t } = useTranslation();
    const { isAuthenticated, isAdmin, isBanned } = useContext(AuthContext);

    const accessToken = localStorage.getItem('access_token');

    useEffect(() => {
        if (accessToken) {
            try {
                const decodedToken = jwtDecode(accessToken);
                setLoggedInUserId(decodedToken.id);
            } catch (error) {
            }
        }
    }, [accessToken]);

    useEffect(() => {
        if (!userId) return;

        setUserProfile(null);
        setUserPosts([]);
        setUserAchievements([]);
        setHasMore(true);
        setPage(1);
        setShowCommentsMap({});
        setLikedPosts(new Set());

        loadUserProfile();
        loadUserAchievements();

        if (loggedInUserId && loggedInUserId === userId) {
            setIsCurrentUser(true);
        } else {
            setIsCurrentUser(false);
        }
    }, [userId, loggedInUserId]);

    useEffect(() => {
        if (!userId || page === 0) return;
        loadUserPosts(page);
    }, [page]);

    const loadUserProfile = () => {
        api
            .get(`${process.env.REACT_APP_API_URL}/api/user/${userId}/info`, {
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
            })
            .then((response) => {
                setUserProfile(response.data);
            })
            .catch((error) => {
                const errorMessage =
                    error.response?.data?.error || t('userProfile.error.fetchProfile');
                Swal.fire({
                    title: t('userProfile.error.title'),
                    text: errorMessage,
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('userProfile.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            });
    };

    const loadUserPosts = (pageToLoad) => {
        if (loading || !hasMore) return;

        setLoading(true);
        api
            .get(`${process.env.REACT_APP_API_URL}/api/user/${userId}/posts?page=${pageToLoad}&limit=5`, {
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
            })
            .then((response) => {
                if (response.data.length === 0) {
                    setHasMore(false);
                } else {
                    const newPosts = response.data;
                    setUserPosts((prevPosts) => [...prevPosts, ...newPosts]);
                    if (newPosts.length < 5) {
                        setHasMore(false);
                    }
                }
            })
            .catch((error) => {
                if (error.response?.status !== 404) {
                    const errorMessage =
                        error.response?.data?.error || t('userProfile.error.fetchPosts');
                    Swal.fire({
                        title: t('userProfile.error.title'),
                        text: errorMessage,
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#00ff00',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: t('userProfile.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                }
                setHasMore(false);
            })
            .finally(() => setLoading(false));
    };

    const loadUserAchievements = () => {
        api
            .get(`${process.env.REACT_APP_API_URL}/api/user/${userId}/achievements`, {
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
            })
            .then((response) => setUserAchievements(response.data))
            .catch((error) => {
                const errorMessage =
                    error.response?.data?.error || t('userProfile.error.fetchAchievements');
                Swal.fire({
                    title: t('userProfile.error.title'),
                    text: errorMessage,
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('userProfile.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            });
    };

    const toggleComments = (postId) => {
        setShowCommentsMap(prev => ({
            ...prev,
            [postId]: !prev[postId]
        }));
    };

    const handleShare = (postId) => {
        const shareUrl = `${window.location.origin}/posts/${postId}`;
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                Swal.fire({
                    text: 'Post link copied to clipboard!',
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            })
            .catch(error => console.error('Error copying link', error));
    };

    const handleLike = (postId) => {
        if (!isAuthenticated) {
            Swal.fire({
                text: t('messages.notLoggedIn'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#d33',
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        const endpoint = `${process.env.REACT_APP_API_URL}/api/post/${postId}/like`;
        const method = likedPosts.has(postId) ? 'delete' : 'post';

        api({
            method,
            url: endpoint,
            headers: { 'Authorization': `Bearer ${accessToken}` }
        })
            .then(() => {
                setLikedPosts(prev => {
                    const newSet = new Set(prev);
                    if (likedPosts.has(postId)) {
                        newSet.delete(postId);
                    } else {
                        newSet.add(postId);
                    }
                    return newSet;
                });
                setUserPosts(prevPosts =>
                    prevPosts.map(post =>
                        post.id === postId ? { ...post, likes: likedPosts.has(postId) ? post.likes - 1 : post.likes + 1 } : post
                    )
                );
            })
            .catch(() => {
                Swal.fire({
                    text: t('posts.alreadyLiked'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('posts.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            });
    };

    useEffect(() => {
        if (loading || !hasMore) return;

        const handleObserver = (entries) => {
            const [entry] = entries;
            if (entry.isIntersecting && hasMore) {
                setPage((prevPage) => prevPage + 1);
            }
        };

        observerInstance.current = new IntersectionObserver(handleObserver, {
            root: null,
            rootMargin: '0px',
            threshold: 1.0,
        });

        if (lastPostElementRef.current) {
            observerInstance.current.observe(lastPostElementRef.current);
        }

        return () => {
            if (observerInstance.current && lastPostElementRef.current) {
                observerInstance.current.unobserve(lastPostElementRef.current);
            }
        };
    }, [loading, hasMore]);

    const renderMedia = (media_url) => {
        if (!media_url) return null;

        // YouTube
        if (media_url.includes('youtube.com') || media_url.includes('youtu.be')) {
            let embedUrl = '';

            if (media_url.includes('/playlist?list=')) {
                const urlParams = new URLSearchParams(media_url.split('?')[1]);
                const playlistId = urlParams.get('list');
                if (playlistId) {
                    embedUrl = `https://www.youtube.com/embed/videoseries?list=${playlistId}`;
                }
            } else {
                let videoId = '';

                if (media_url.includes('/shorts/')) {
                    const path = new URL(media_url).pathname;
                    const parts = path.split('/');
                    const shortsIndex = parts.indexOf('shorts');
                    if (shortsIndex !== -1 && shortsIndex + 1 < parts.length) {
                        const videoIdWithQuery = parts[shortsIndex + 1];
                        videoId = videoIdWithQuery.split('?')[0];
                    }
                } else if (media_url.includes('youtu.be')) {
                    videoId = media_url.split('youtu.be/')[1].split('?')[0];
                } else {
                    const urlParams = new URLSearchParams(media_url.split('?')[1]);
                    videoId = urlParams.get('v');
                }

                if (videoId) {
                    embedUrl = `https://www.youtube.com/embed/${videoId}`;
                }
            }

            if (embedUrl) {
                return (
                    <iframe
                        width="560"
                        height="315"
                        src={embedUrl}
                        title={media_url.includes('/playlist?list=') ? "YouTube playlist" : "YouTube video"}
                        frameBorder="0"
                        allowFullScreen
                    ></iframe>
                );
            } else {
                return <p>Error while loading video</p>;
            }
        }

        if (media_url.includes('twitch.tv')) {
            const parentDomain = 'superpeople.live';

            if (media_url.includes('/clip/')) {
                const clipId = media_url.split('/clip/')[1].split('?')[0];
                const embedUrl = `https://clips.twitch.tv/embed?clip=${clipId}&parent=${parentDomain}`;
                return (
                    <iframe
                        src={embedUrl}
                        height="480"
                        width="720"
                        title={`Twitch clip ${clipId}`}
                        frameBorder="0"
                        scrolling="no"
                        allowFullScreen
                    ></iframe>
                );
            } else if (media_url.includes('/videos/')) {
                const videoId = media_url.split('/videos/')[1].split('?')[0];
                const embedUrl = `https://player.twitch.tv/?video=${videoId}&parent=${parentDomain}`;
                return (
                    <iframe
                        src={embedUrl}
                        height="480"
                        width="720"
                        title={`Twitch video ${videoId}`}
                        frameBorder="0"
                        scrolling="no"
                        allowFullScreen
                    ></iframe>
                );
            } else {
                const channelName = media_url.split('twitch.tv/')[1].split('?')[0];
                const embedUrl = `https://player.twitch.tv/?channel=${channelName}&parent=${parentDomain}`;
                return (
                    <iframe
                        src={embedUrl}
                        height="480"
                        width="720"
                        title={`Twitch channel ${channelName}`}
                        frameBorder="0"
                        scrolling="no"
                        allowFullScreen
                    ></iframe>
                );
            }
        }

        if (media_url.match(/\.(jpeg|jpg|gif|png)$/i)) {
            return (
                <Zoom>
                    <img
                        src={media_url}
                        alt={t('userProfile.userMedia')}
                        className="user-media-image"
                    />
                </Zoom>
            );
        }

        if (media_url.match(/\.(mp4|webm|ogg)$/i)) {
            return (
                <video controls width="600">
                    <source src={media_url} type="video/mp4" />
                    {t('userProfile.videoNotSupported')}
                </video>
            );
        }

        return (
            <a href={media_url} target="_blank" rel="noopener noreferrer">
                {media_url}
            </a>
        );
    };

    if (!userProfile) {
        return <LoadingSpinner />;
    }

    const formatDate = (dateObj) => {
        if (!dateObj || !dateObj.date || !dateObj.timezone) {
            return t('admin.unknown');
        }

        let lang = i18n.language;
        const languageMapping = {
            'ua': 'uk',
        };
        const intlLanguage = languageMapping[lang] || lang;
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        moment.locale(intlLanguage);
        const date = moment.utc(dateObj.date).tz(userTimezone);

        return date.format('LLL');
    };

    return (
        <div className="user-profile-page">
            <div className="user-profile-info">
                <div className="profile-header">
                    <div className="profile-info">
                        <img
                            src={userProfile.profile_icon || logo}
                            alt={t('userProfile.profileIcon')}
                            className="profile-icon"
                        />
                        <div className="profile-details">
                            <h1 className="profile-username">
                                {userProfile.username}'s {t('userProfile.profile')}
                            </h1>
                            <p className="profile-level">{t('userProfile.level')}: {userProfile.level}</p>
                            <p className="profile-experience">
                                {t('userProfile.experience')}: {userProfile.experience}/
                                {userProfile.next_level_experience}
                            </p>
                            <progress
                                value={userProfile.experience}
                                max={userProfile.next_level_experience}
                                className="experience-bar"
                            ></progress>
                            <p className="profile-favorite-character">
                                {t('userProfile.favoriteCharacter')}: {userProfile.favorite_character || t('userProfile.notSpecified')}
                            </p>
                            {isCurrentUser && (
                                <a href="/settings" className="edit-profile-button">
                                    {t('userProfile.editProfile')}
                                </a>
                            )}
                        </div>
                    </div>
                </div>
                <p className="section-title">{t('userProfile.achievements')}</p>
                {userAchievements.length > 0 ? (
                    <div className="user-achievements">
                        {userAchievements.map((achievement) => (
                            <div key={achievement.id} className="achievement-card">
                                <img
                                    src={achievement.icon}
                                    alt={`${achievement.name} ${t('userProfile.icon')}`}
                                    className="achievement-icon"
                                />
                                <div className="achievement-details">
                                    <h3>{achievement.name}</h3>
                                    <p>{achievement.description}</p>
                                    <p className="achievement-date">
                                        {t('userProfile.achievedAt')}: {achievement.achieved_at}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="no-achievements">{t('userProfile.noAchievements')}</p>
                )}

                <p className="section-title profile-posts">{t('userProfile.posts')}</p>
                {userPosts.length > 0 ? (
                    <div className="user-posts">
                        {userPosts.map((post, index) => (
                            <div
                                ref={userPosts.length === index + 1 ? lastPostElementRef : null}
                                key={post.id}
                                className={`post-card-profile ${index === 0 ? 'no-top-border' : ''}`}
                            >
                                <div className="post-content">
                                    <div className="post-info"
                                         dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(post.content)}}
                                    ></div>
                                    {renderMedia(post.media_url)}
                                </div>
                                <div className="post-date">
                                    <FontAwesomeIcon icon={faCalendarAlt}/> {formatDate(post.created_at)}
                                </div>
                                <div className="post-actions">
                                    <span
                                        className={`like-button ${likedPosts.has(post.id) ? 'liked' : ''}`}
                                        onClick={() => handleLike(post.id)}
                                        style={{cursor: isAuthenticated ? 'pointer' : 'default'}}
                                    >
                                        <FontAwesomeIcon icon={faHeart} className="icon"/>
                                        <span className="like-count">{post.likes || 0}</span>
                                    </span>
                                    <button className="share-button" onClick={() => handleShare(post.id)}>
                                        <FontAwesomeIcon icon={faShareAlt} className="icon"/>
                                        {t('posts.share')}
                                    </button>
                                    <button className="comments-button" onClick={() => toggleComments(post.id)}>
                                        <FontAwesomeIcon icon={faComment} className="icon"/>
                                        {t('posts.comments')} ({post.comments || 0})
                                    </button>
                                </div>
                                {showCommentsMap[post.id] && (
                                    <PostComment
                                        postId={post.id}
                                        isAuthenticated={isAuthenticated}
                                        userId={loggedInUserId}
                                        isAdmin={isAdmin}
                                        isBanned={isBanned}
                                        token={accessToken}
                                        onCommentCountUpdate={(newCount) => {
                                            setUserPosts(prevPosts =>
                                                prevPosts.map(p =>
                                                    p.id === post.id ? { ...p, comments: newCount } : p
                                                )
                                            );
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="no-posts">{t('userProfile.noPosts')}</p>
                )}
                {loading && hasMore && <p>{t('userProfile.loadingMorePosts')}</p>}
            </div>
        </div>
    );
}

export default UserProfilePage;
